<template>
  <div :class="$style.swiperContainer">
    <Swiper
      :modules="[Autoplay, EffectFade, Navigation, Pagination]"
      :loop="slides.length > 1"
      :speed="effect === 'fade' ? 1300 : 600"
      :autoplay="{ delay: 6000, disableOnInteraction: false }"
      :centered-slides="true"
      :space-between="spaceBetween"
      :slides-per-view="slidesPerViewSp"
      :slides-per-group="slidesPerGroup"
      :breakpoints="{ 640: { slidesPerView } }"
      :effect="effect"
      :fade-effect="effect === 'fade' ? { crossFade: true } : undefined"
      :pagination="pagination ? { el: '.swiper-pagination', type: 'bullets', clickable: true } : false"
      :navigation="navigation"
    >
      <SwiperSlide v-for="(slide, index) in slides" :key="index">
        <component :is="slide.url ? 'a' : 'div'" :href="slide.url">
          <p :class="[$style.image, slide.published_at === '' ? 'draft-content' : '']">
            <img :src="slide.image_url" :alt="slide.image_alt || ''" :width="imageWidth" :height="imageHeight" :loading="index === 0 ? 'eager' : 'lazy'" />
          </p>
          <div v-if="slide.title" :class="$style.text">
            <p v-if="slide.series_name" :class="$style.seriesTitle">{{ slide.series_name }}</p>
            <p :class="$style.title">{{ slide.title }}</p>
          </div>
        </component>
      </SwiperSlide>
    </Swiper>
    <div v-if="pagination" class="swiper-pagination" />
  </div>
</template>

<script setup lang="ts">
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { type Slide } from '@/types/swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

withDefaults(
  defineProps<{
    slides: Slide[];
    imageWidth: number;
    imageHeight: number;
    effect?: 'slide' | 'fade';
    pagination?: boolean;
    navigation?: boolean;
    spaceBetween?: number;
    slidesPerView?: number;
    slidesPerViewSp?: number;
    slidesPerGroup?: number;
  }>(),
  {
    effect: 'slide',
    pagination: true,
    navigation: true,
    spaceBetween: 0,
    slidesPerView: 1,
    slidesPerViewSp: 1,
    slidesPerGroup: 1,
  },
);
</script>

<style module lang="scss">
.swiperContainer {
  position: relative;

  a {
    display: block;
    text-decoration: none;
    transition: opacity 0.4s;

    &:hover {
      opacity: 0.8;
    }
  }
}

.image img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.text {
  margin-top: 12px;
}

.seriesTitle {
  color: #515151;
  font-size: 12px;
  margin-bottom: 2px;
  text-align: center;
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
  padding: 0 5%;
  text-align: center;
}

:global(.swiper-button-prev),
:global(.swiper-button-next) {
  @media (--sp) {
    display: none;
  }
}
</style>
